import React, { useRef } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import classnames from "classnames/bind";

import ProfileDropdown from "../Profile/components/ProfileDropdown";
import Avatar from "../Profile/components/Avatar";
import Counter from "../Counter";

import BottomMenuButton from "./components/BottomMenuButton";
import BottomAllServicesDropdown from "./components/BottomAllServicesDropdown";
import DropdownContent from "./components/DropdownContent";
import ProfileFirmInfo from "./components/ProfileFirmInfo";

import sendMetric, { sendNavigationMetric } from "../../utils/sendMetric";

import { useStore } from "../../connect";

import { MAIN_PAGE_QUERY_PARAM } from "../../consts";

import strings from "../../locales/strings";

import styles from "./BottomMenu.scss";

const cx = classnames.bind(styles);

const BottomMenu = () => {
  const { adaptive, profile, header, baseUrl } = useStore();
  const bottomMenuRef = useRef(null);

  const isPrerender = process.env.PRERENDER === "1";
  const currentBaseUrl = isPrerender ? "SSI_PATH_TO_BASE_URL" : baseUrl;

  const selectDropdown = (dropdownName, event) => {
    event.preventDefault();
    event.stopPropagation();

    header.toggleDropdown(dropdownName);
  };

  const handleAllServicesClick = (event) => {
    selectDropdown("all-services-bottom", event);
  };

  const handleOrdersClick = (event) => {
    selectDropdown("orders-bottom", event);
  };

  /* Add sendMetric? */
  const handleProfileClick = (event) => {
    if (profile.isLoggedIn) {
      selectDropdown("profile-bottom", event);
    } else {
      profile.onLoginOpen({
        next: window.location.href,
        additionalQueryParams: header.isMainPage ? MAIN_PAGE_QUERY_PARAM : "",
      });
    }
  };

  const isAnonim = !profile.isLoggedIn;

  return (
    <>
      {header.openedDropdown === "all-services-bottom" && (
        <BottomAllServicesDropdown
          isSubmenuVisible={isAnonim}
          className={cx("visible-on-mobile", "hidden-by-compact-header")}
        />
      )}
      <div
        className={cx(
          "bottom-menu",
          "visible-on-mobile",
          "hidden-by-compact-header"
        )}
        ref={bottomMenuRef}
        id="header-bottom-menu"
      >
        {header.openedDropdown === "profile-bottom" && (
          <DropdownContent
            style={{
              maxHeight:
                window.innerHeight - bottomMenuRef.current.clientHeight,
            }}
            onClose={header.closeDropdown}
            className={cx("dropdown-profile")}
            contentClassName={cx("profile-dropdown-content")}
          >
            <ProfileFirmInfo />
            <div className={cx("profile-wrapper")}>
              <ProfileDropdown />
            </div>
          </DropdownContent>
        )}
        <BottomMenuButton
          iconName="ic_cargo-out"
          label={strings.bottom_yourLoad}
          smallLabel={strings.bottom_loads}
          href={
            !isAnonim
              ? `https://loads.${baseUrl}/published?utm_source=header&utm_campaign=new_header`
              : undefined
          }
          onClick={
            isAnonim
              ? header.handleYourLoadsClick
              : () => sendNavigationMetric("BOTTOM_MENU_YOUR_LOAD")
          }
        />
        <BottomMenuButton
          iconName="ic_truck-out"
          label={strings.bottom_yourTrucks}
          smallLabel={strings.bottom_trucks}
          href={
            !isAnonim
              ? `https://trucks.${baseUrl}/published?utm_source=header&utm_campaign=new_header`
              : undefined
          }
          onClick={
            isAnonim
              ? header.handleYourTrucksClick
              : () => sendNavigationMetric("BOTTOM_MENU_YOUR_TRUCKS")
          }
        />
        <BottomMenuButton
          iconName="ic_services-out"
          label={strings.bottom_services}
          active={header.openedDropdown === "all-services-bottom"}
          onClick={handleAllServicesClick}
        />
        <BottomMenuButton
          iconName="ic_orders-out"
          label={strings.bottom_orders}
          active={header.openedDropdown === "orders-bottom"}
          onClick={
            isAnonim
              ? () => sendNavigationMetric("BOTTOM_MENU_ORDERS")
              : handleOrdersClick
          }
          href={
            isAnonim
              ? `https://${currentBaseUrl}/landings/udeal/?utm_source=header&utm_campaign=new_header`
              : undefined
          }
          counter={profile.counters.orders}
          dropdown={
            <DropdownContent
              onClose={header.closeDropdown}
              className={cx("dropdown-orders")}
              contentClassName={cx("orders-dropdown-content")}
              items={[
                {
                  icon: "ic_orders-from-you-out",
                  label: strings.ordersFromYou,
                  href: `https://loads.${baseUrl}/orders/cargoOwner/Reconciliation?utm_source=header&utm_campaign=new_header`,
                  counter: !!profile.counters.yourOrders && (
                    <Counter maxValue={99} active>
                      {profile.counters.yourOrders}
                    </Counter>
                  ),
                  onClick: () =>
                    sendNavigationMetric("BOTTOM_MENU_YOUR_ORDERS"),
                },
                {
                  icon: "ic_orders-to-you-out",
                  label: strings.ordersToYou,
                  href: `https://loads.${baseUrl}/orders/carrier/Reconciliation?utm_source=header&utm_campaign=new_header`,
                  counter: !!profile.counters.ordersToYou && (
                    <Counter maxValue={99} active>
                      {profile.counters.ordersToYou}
                    </Counter>
                  ),
                  onClick: () =>
                    sendNavigationMetric("BOTTOM_MENU_ORDERS_TO_YOU"),
                },
                {
                  icon: "ic_add-circle-outline",
                  label: strings.ordersToDriver,
                  href: `https://loads.${baseUrl}/orders/application/create?utm_source=main_zakaz&utm_medium=click`,
                  onClick: () => {
                    sendMetric("ord-main-ordertodriver");
                    sendNavigationMetric("BOTTOM_MENU_ORDERS_TO_DRIVER");
                  },
                },
              ]}
            />
          }
        />
        <BottomMenuButton
          avatar={<Avatar forBottomMenu />}
          label={strings.bottom_profile}
          active={header.openedDropdown === "profile-bottom"}
          onClick={handleProfileClick}
        />
      </div>
      {(adaptive.isLargeMobile || adaptive.isMobile) &&
        ReactDOM.createPortal(
          <div
            style={{ height: 67 }}
            className={cx("body-empty-space", "hidden-by-compact-header")}
          />,
          document.body
        )}
    </>
  );
};

export default observer(BottomMenu);
